import { Link } from '@tanstack/react-router';
import type { PublicAuthor } from '@dove/shared';
import { cn } from '@/lib/utils';

/**
 * A link to somebody's social card (`/u/:handle`, docs/1 § One projection of a
 * person).
 *
 * EVERY RENDERING OF A NAME OR A FACE IS ONE, wherever it appears — a feed post,
 * a letterhead, an envelope. A person's name being tappable is how anyone gets to
 * them at all: there is no people search on any surface and no directory of
 * people anywhere in the product (docs/5 § D260730-2028), so a name that does nothing is a
 * dead end rather than a restraint.
 *
 * IT LIVES HERE RATHER THAN ON EACH SURFACE because it was written twice with two
 * different sets of classes on it, and a third copy was about to be written for
 * letters. One route, one focus shape, one place that knows a person's card is at
 * `/u/:handle`.
 *
 * YOUR OWN NAME LINKS HERE TOO, rather than to `/me`. `ProfileScreen` resolves
 * the viewer itself — your own handle IS `/me`, and it renders the self card — so
 * a letterhead saying "You" does not need to know whether it is describing the
 * viewer in order to be tappable.
 *
 * Opening a card is not an event and records nothing: "who viewed your profile"
 * is permanently declined (docs/8 § D).
 */
export function ToProfile({
  author,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<'a'>, 'href'> & { author: PublicAuthor }) {
  return (
    <Link
      to="/u/$handle"
      params={{ handle: author.handle }}
      className={cn('min-w-0 rounded-sm', className)}
      {...props}
    >
      {children}
    </Link>
  );
}
